import React, { useState, useEffect } from 'react'
import { useTenant } from '../../contexts/TenantContext'
import api from '../../services/apiService'

const Dashboard = () => {
  const { tenant } = useTenant()
  const [user, setUser] = useState(null)
  const [stats, setStats] = useState({
    players: 0,
    officials: 0,
    events: 0,
    users: 0
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const storedUser = localStorage.getItem('tenant_admin_user')
    if (storedUser) {
      setUser(JSON.parse(storedUser))
    }
    loadStats()
  }, [])

  const loadStats = async () => {
    try {
      setLoading(true)
      console.log('📊 Loading dashboard statistics...')

      // Fetch all stats in parallel
      const [playersRes, officialsRes, eventsRes, usersRes] = await Promise.allSettled([
        api.get('/client-admin/players'),
        api.get('/client-admin/officials/state-wise-count'),
        api.get('/client-admin/events'),
        api.get('/client-admin/users')
      ])

      // Process players data
      let playersCount = 0
      if (playersRes.status === 'fulfilled' && playersRes.value.data.success) {
        // Use total field for accurate count
        playersCount = playersRes.value.data.total || playersRes.value.data.data?.length || 0
        console.log('✅ Players loaded:', playersCount)
      }

      // Process officials data
      let officialsCount = 0
      if (officialsRes.status === 'fulfilled' && officialsRes.value.data.success) {
        const officialsData = officialsRes.value.data.data || []
        officialsCount = officialsData.reduce((sum, state) => sum + (state.total || 0), 0)
        console.log('✅ Officials loaded:', officialsCount)
      }

      // Process events data
      let eventsCount = 0
      if (eventsRes.status === 'fulfilled') {
        // API returns { events: {...}, stats: { total: 5, ... } }
        eventsCount = eventsRes.value.data.stats?.total || 0
        console.log('✅ Events loaded:', eventsCount)
      }

      // Process users data
      let usersCount = 0
      if (usersRes.status === 'fulfilled' && usersRes.value.data.success) {
        // Use total field for accurate count
        usersCount = usersRes.value.data.total || usersRes.value.data.data?.length || 0
        console.log('✅ Users loaded:', usersCount)
      }

      setStats({
        players: playersCount,
        officials: officialsCount,
        events: eventsCount,
        users: usersCount
      })

      console.log('📊 Stats SET to:', {
        players: playersCount,
        officials: officialsCount,
        events: eventsCount,
        users: usersCount
      })

      console.log('📊 Dashboard stats loaded successfully')
      setLoading(false)
    } catch (error) {
      console.error('❌ Error loading dashboard stats:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="client-admin-dashboard">
      <div className="dashboard-header">
        <div className="header-icon">
          <i className="fas fa-chart-line"></i>
        </div>
        <h1>Dashboard</h1>
        <p className="dashboard-subtitle">
          Welcome back! Here's what's happening with {tenant?.name || 'your organization'}.
        </p>
      </div>

      <div className="feature-cards">
        <div className="feature-card feature-card-players">
          <div className="feature-header">
            <div className="feature-icon">
              <i className="fas fa-users"></i>
            </div>
            <div className="feature-count">{loading ? '...' : stats.players}</div>
            <div className="feature-label">Players</div>
          </div>
          <div className="feature-actions">
            <div className="feature-action-item">
              <i className="fas fa-check"></i>
              <span>View players state-wise</span>
            </div>
            <div className="feature-action-item">
              <i className="fas fa-check"></i>
              <span>View player's detail</span>
            </div>
            <div className="feature-action-item">
              <i className="fas fa-check"></i>
              <span>Edit player's detail</span>
            </div>
            <div className="feature-action-item">
              <i className="fas fa-check"></i>
              <span>Delete a player</span>
            </div>
          </div>
          <a href="/admin/players" className="feature-link">
            View Details
            <i className="fas fa-arrow-circle-right"></i>
          </a>
        </div>

        <div className="feature-card feature-card-officials">
          <div className="feature-header">
            <div className="feature-icon">
              <i className="fas fa-user-tie"></i>
            </div>
            <div className="feature-count">{loading ? '...' : stats.officials}</div>
            <div className="feature-label">Officials</div>
          </div>
          <div className="feature-actions">
            <div className="feature-action-item">
              <i className="fas fa-check"></i>
              <span>View officials state wise</span>
            </div>
            <div className="feature-action-item">
              <i className="fas fa-check"></i>
              <span>View official's detail</span>
            </div>
            <div className="feature-action-item">
              <i className="fas fa-check"></i>
              <span>Edit official's detail</span>
            </div>
            <div className="feature-action-item">
              <i className="fas fa-check"></i>
              <span>Delete a official</span>
            </div>
          </div>
          <a href="/admin/officials" className="feature-link">
            View Details
            <i className="fas fa-arrow-circle-right"></i>
          </a>
        </div>

        <div className="feature-card feature-card-tournaments">
          <div className="feature-header">
            <div className="feature-icon">
              <i className="fas fa-calendar-alt"></i>
            </div>
            <div className="feature-count">{loading ? '...' : stats.events}</div>
            <div className="feature-label">Tournaments</div>
          </div>
          <div className="feature-actions">
            <div className="feature-action-item">
              <i className="fas fa-check"></i>
              <span>Create a tournament</span>
            </div>
            <div className="feature-action-item">
              <i className="fas fa-check"></i>
              <span>Create forthcoming tournament</span>
            </div>
            <div className="feature-action-item">
              <i className="fas fa-check"></i>
              <span>Edit a tournament</span>
            </div>
            <div className="feature-action-item">
              <i className="fas fa-check"></i>
              <span>Disable a tournament</span>
            </div>
          </div>
          <a href="/admin/events" className="feature-link">
            View Details
            <i className="fas fa-arrow-circle-right"></i>
          </a>
        </div>

        <div className="feature-card feature-card-users">
          <div className="feature-header">
            <div className="feature-icon">
              <i className="fas fa-users-cog"></i>
            </div>
            <div className="feature-count">{loading ? '...' : stats.users}</div>
            <div className="feature-label">Users</div>
          </div>
          <div className="feature-actions">
            <div className="feature-action-item">
              <i className="fas fa-check"></i>
              <span>View all users</span>
            </div>
            <div className="feature-action-item">
              <i className="fas fa-check"></i>
              <span>View user profile & events</span>
            </div>
            <div className="feature-action-item">
              <i className="fas fa-check"></i>
              <span>Edit user details</span>
            </div>
            <div className="feature-action-item">
              <i className="fas fa-check"></i>
              <span>Manage user status</span>
            </div>
          </div>
          <a href="/admin/users" className="feature-link">
            View Details
            <i className="fas fa-arrow-circle-right"></i>
          </a>
        </div>
      </div>

      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="actions-grid">
          <a href="/admin/events" className="action-card">
            <div className="action-icon">
              <i className="fas fa-plus-circle"></i>
            </div>
            <div className="action-content">
              <div className="action-title">Create Event</div>
              <div className="action-description">Set up new tournaments and competitions</div>
            </div>
          </a>
          <a href="/admin/players" className="action-card">
            <div className="action-icon">
              <i className="fas fa-users"></i>
            </div>
            <div className="action-content">
              <div className="action-title">Manage Players</div>
              <div className="action-description">View, edit and manage player registrations</div>
            </div>
          </a>
          <a href="/admin/officials" className="action-card">
            <div className="action-icon">
              <i className="fas fa-users-cog"></i>
            </div>
            <div className="action-content">
              <div className="action-title">Manage Officials</div>
              <div className="action-description">Register and manage officials</div>
            </div>
          </a>
          <a href="/admin/cms" className="action-card">
            <div className="action-icon">
              <i className="fas fa-edit"></i>
            </div>
            <div className="action-content">
              <div className="action-title">Manage Content</div>
              <div className="action-description">Edit pages and website content</div>
            </div>
          </a>
          <a href="/admin/settings" className="action-card">
            <div className="action-icon">
              <i className="fas fa-cog"></i>
            </div>
            <div className="action-content">
              <div className="action-title">Settings</div>
              <div className="action-description">Configure site preferences</div>
            </div>
          </a>
          <a href="/admin/users" className="action-card">
            <div className="action-icon">
              <i className="fas fa-users-cog"></i>
            </div>
            <div className="action-content">
              <div className="action-title">User Management</div>
              <div className="action-description">Manage users and their event participation</div>
            </div>
          </a>
        </div>
      </div>
    </div>
  )
}

export default Dashboard